import { createContext, useContext, useState, useMemo, useCallback, type ReactNode } from 'react';

interface HomeFilterState {
  selectedMonth: string;
  setSelectedMonth: (month: string) => void;
  selectedStatuses: string[];
  toggleStatus: (status: string) => void;
  /** Comma-joined string for API calls, e.g. "APPROVED,PAID" */
  statusParam: string;
  /** Optional: view data as another user (admin preview) */
  previewUserId?: string;
}

const HomeFilterContext = createContext<HomeFilterState | null>(null);

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function HomeFilterProvider({ children, previewUserId }: { children: ReactNode; previewUserId?: string }) {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['PAID']);

  const toggleStatus = useCallback((status: string) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        // Don't allow deselecting all — keep at least one
        if (prev.length === 1) return prev;
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  }, []);

  const statusParam = useMemo(() => selectedStatuses.join(','), [selectedStatuses]);

  const value = useMemo(
    () => ({ selectedMonth, setSelectedMonth, selectedStatuses, toggleStatus, statusParam, previewUserId }),
    [selectedMonth, selectedStatuses, toggleStatus, statusParam, previewUserId],
  );

  return <HomeFilterContext value={value}>{children}</HomeFilterContext>;
}

export function useHomeFilter() {
  const ctx = useContext(HomeFilterContext);
  if (!ctx) throw new Error('useHomeFilter must be used within HomeFilterProvider');
  return ctx;
}
